/* ============================================================
   quick-add.js — the colour half of M-06's quick-add panel.

   ⚠ THIS FILE DOES NOT ADD ANYTHING TO THE CART, and that is the single most
   important thing to know before editing it. The panel's CTA is a real
   {% form 'product' %} posting to /cart/add, and assets/cart.js already
   enhances any such form through a document-level delegated submit listener.
   From that one fact the panel inherits the AJAX add, the section re-render,
   the drawer-or-toast per the owner's setting, Shopify's own refusal wording
   in [data-cart-error], the per-form double-submit guard, and the response
   staleness protocol — which cart.js's own comment says was written for
   exactly this case, "a grid of per-card add buttons". A fetch here would
   reimplement all of it and get the staleness half wrong.

   Nor does it resolve the variant. The size chips are radios named `id`,
   which is the field the cart reads, so the browser resolves it. On a
   size-only product this file is therefore DEAD WEIGHT: everything works with
   it deleted. Two-option products are its entire reason to exist, because a
   radio group can select a value but it cannot resolve a PAIR.

   So the job is one sentence: when a colour is picked, re-point the size
   radios at that colour's variants, and swap the card image.

   Everything it needs is already in the markup, put there by Liquid:
     · [data-qa-map]      one row per variant, [colour, size, variant id, 1|0]
     · [data-qa-sizes]    the size radios, each carrying data-qa-value
     · [data-qa-colour]   one stacked image layer per colourway
   It builds no URLs, holds no prices and knows no titles. A script that
   carries a price is a script that can show a stale one.

   Loaded by sections/product-grid.liquid rather than the layout, so a page
   with no grid — or a grid with quick add switched off — never downloads it.

   Last updated: 2026-08-12
   ============================================================ */
(function () {
  'use strict';

  /* The homepage can carry several product grids and each emits this tag. The
     file is fetched once but two identical script elements both EXECUTE, which
     would register the delegated listener twice and run every colour change
     through it twice. Same guard shape as capture.js.

     Not slideshow.js's per-name customElements.get check — that is the right
     answer for a file that defines custom elements and the wrong one here,
     because this file defines none and has nothing to ask about. */
  if (window.__nmpQuickAdd) return;
  window.__nmpQuickAdd = true;

  /* The colour radios are named per card so two cards' groups never merge.
     The prefix is what identifies them without a class lookup. */
  var PREFIX = 'qa-c-';

  /* Parsed once per panel and cached on the element. A collection page can
     hold two dozen panels and a shopper may sweep across all of them; parsing
     on every change would re-read the same JSON repeatedly. Caching on the
     NODE rather than in a module-level map is deliberate: assets/collection.js
     replaces the whole grid on a filter change, so a keyed cache would hold
     stale rows for nodes that no longer exist while the fresh ones re-parse
     anyway. A property on the node is discarded exactly when the node is. */
  function mapOf(panel) {
    if (panel.__nmpQaRows) return panel.__nmpQaRows;

    var el = panel.querySelector('script[type="application/json"][data-qa-map]');
    if (!el) return null;

    var rows = null;
    try {
      rows = JSON.parse(el.textContent);
    } catch (error) {
      /* Swallowed on purpose. A malformed map means the colour chips stop
         doing anything, which is precisely the no-JS behaviour the panel is
         already designed to survive: the card still adds its default colour.
         Throwing here would take the listener down for every OTHER card on
         the page as well. */
      return null;
    }

    if (!rows || !rows.length) return null;
    panel.__nmpQaRows = rows;
    return rows;
  }

  function lookup(rows, colour, size) {
    for (var i = 0; i < rows.length; i++) {
      if (rows[i][0] === colour && rows[i][1] === size) return rows[i];
    }
    return null;
  }

  /* The size radios, re-pointed at the newly chosen colour. Returns the radio
     that ended up checked, or null when the colour is sold out in every size. */
  function revalueSizes(panel, rows, colour) {
    var group = panel.querySelector('[data-qa-sizes]');
    if (!group) return null;

    var radios = group.querySelectorAll('input[type="radio"]');
    var i;

    for (i = 0; i < radios.length; i++) {
      var radio = radios[i];
      var row = lookup(rows, colour, radio.getAttribute('data-qa-value'));
      var buyable = !!(row && row[3]);

      /* An empty value rather than a stale one when the combination does not
         exist at all. The radio is disabled in the same breath, so it can
         never be submitted — but leaving the other colour's variant id on it
         would mean a single missed disable bags the wrong garment. */
      radio.value = row ? String(row[2]) : '';
      radio.disabled = !buyable;

      /* The label is the next sibling — the markup is input-then-label so the
         :checked + label selector works without :has(). Both halves of the
         sold-out state move together: the strikethrough for eyes, the hidden
         span for a screen reader. */
      var label = radio.nextElementSibling;
      if (label && label.tagName === 'LABEL') {
        if (buyable) {
          label.removeAttribute('data-soldout');
        } else {
          label.setAttribute('data-soldout', '');
        }
        var note = label.querySelector('[data-qa-so]');
        if (note) note.hidden = buyable;
      }
    }

    /* Keep the size the shopper already picked if it survived the colour
       change, and only fall back to the first available one if it did not.
       Jumping to the first available every time takes an M away from someone
       who asked for an M and is simply comparing colourways. */
    var keep = null;
    var first = null;
    for (i = 0; i < radios.length; i++) {
      if (radios[i].disabled) continue;
      if (!first) first = radios[i];
      if (radios[i].checked) keep = radios[i];
    }

    var pick = keep || first;

    /* Set every one, not just the winner: a radio that was checked and has
       just been disabled keeps its checked state in the DOM otherwise. */
    for (i = 0; i < radios.length; i++) {
      radios[i].checked = radios[i] === pick;
    }

    return pick;
  }

  /* The card image. Layers only exist for products Liquid judged eligible —
     two colourways or fewer, not the eager first card, and every value with a
     real variant image — so most cards fall straight out of here and simply
     re-filter their sizes.

     The FIRST layer is the card's default colourway, because Liquid renders
     the layers and the colour radios from the same list and checks the first
     one. Returning to it removes the attribute entirely rather than revealing
     an identical picture, which puts the card back to the exact markup state
     it loads in — hover swap to the second product image included. */
  function swapImage(card, colour) {
    var layers = card.querySelectorAll('[data-qa-colour]');
    if (!layers.length) return;

    if (layers[0].getAttribute('data-qa-colour') === colour) {
      card.removeAttribute('data-qa-swatched');
      for (var i = 0; i < layers.length; i++) {
        layers[i].removeAttribute('data-active');
      }
      return;
    }

    var matched = false;
    for (var j = 0; j < layers.length; j++) {
      var on = layers[j].getAttribute('data-qa-colour') === colour;
      if (on) {
        matched = true;
        layers[j].setAttribute('data-active', '');
      } else {
        layers[j].removeAttribute('data-active');
      }
    }

    /* No layer for this colour means there is no picture to show, and hiding
       the primary image would leave a blank card. Liquid's all-or-nothing rule
       should make this unreachable; it is here because "unreachable" and
       "blank product card" are a bad pair to be wrong about. */
    if (matched) {
      card.setAttribute('data-qa-swatched', '');
    } else {
      card.removeAttribute('data-qa-swatched');
    }
  }

  function apply(card, colour) {
    var panel = card.querySelector('[data-quick-add-panel]');
    if (!panel) return;

    var rows = mapOf(panel);
    if (!rows) return;

    var pick = revalueSizes(panel, rows, colour);

    /* The CTA is always live except in one state: a colourway sold out in
       every size. Liquid disables it on load for the same case and the button
       is styled for it already, so this only has to keep the two in step. */
    var button = panel.querySelector('button[type="submit"]');
    if (button) button.disabled = !pick;

    swapImage(card, colour);
  }

  /* Delegated on document, at module scope, for the reason collection.js's
     header warns about: a filter change replaces the whole grid, and every
     listener bound to a card inside it goes with the node. One listener above
     the swap keeps working on markup that did not exist when it was
     registered. Same shape as cart.js's. */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || target.type !== 'radio') return;
    if (String(target.name).slice(0, PREFIX.length) !== PREFIX) return;

    var card = target.closest('[data-quick-add]');
    if (!card) return;

    apply(card, target.value);
  });
})();
